import { Tearsheet } from '@carbon/ibm-products';
import { defineMessages, useIntl } from 'react-intl';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import styles from './SpecificTeamMemberView.module.scss';

import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import { useGetTeamMembersQuery } from 'api/entityEndpoints';
import { TeamMemberProvider, useTeamMemberContext } from 'Screens/Dashboard/Team/Components/TeamCellHandler/TeamInteractions/Interactions/Details/TeamMemberDetailsContext';
import TeamMemberDetails from 'Screens/Dashboard/Team/Components/TeamCellHandler/TeamInteractions/Interactions/Details/TeamMemberDetails';
import sharedMessages from 'Components/utils/sharedMessages';

type SpecificTeamMemberModalProps = {
    memberId?: string;
    open: boolean;
    onClose: () => void;
};

export const SpecificTeamMemberModal = ({
    memberId,
    open,
    onClose,
}: SpecificTeamMemberModalProps) => {
    const currentEntityId = useGetCurrentEntityId();
    const { data } = useGetTeamMembersQuery(currentEntityId ?? '', { skip: !currentEntityId });

    const member = useMemo(() => {
        const currentTeamMember = (data?.items ?? []).find((tm) => tm.id === memberId);
        if (!currentTeamMember) {
            return null;
        }
        return {
            ...currentTeamMember,
            teamMemberId: currentTeamMember.id,
            name: `${currentTeamMember.firstName} ${currentTeamMember.lastName}`,
            role: currentTeamMember.role,
            appRole: currentTeamMember.role,
            parents: [], // TO_DO
        };
    }, [data, memberId]);

    if (!open || !member) {
        return null;
    }

    return (
        <TeamMemberProvider initialState={member}>
            <SpecificTeamMemberView onClose={onClose} />
        </TeamMemberProvider>
    );
};

const SpecificTeamMemberViewContainer = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const onClose = () => {
        navigate('/team');
    };

    return (
        <SpecificTeamMemberModal
            memberId={id}
            open
            onClose={onClose}
        />
    );
};

const SpecificTeamMemberView = ({
    onClose,
}: {
    onClose: () => void;
}) => {
    const intl = useIntl();
    const { values } = useTeamMemberContext();
    const { name } = values;

    return (
        <Tearsheet
            open
            title={name}
            description={intl.formatMessage(messages.description)}
            closeIconDescription={intl.formatMessage(sharedMessages.close)}
            hasCloseIcon
            onClose={onClose}
            selectorPrimaryFocus="#name-input-in-team-member"
            actions={[
                {
                    label: intl.formatMessage(sharedMessages.close),
                    kind: 'primary',
                    onClick: onClose,
                },
            ]}
        >
            <div className={styles.content}>
                <TeamMemberDetails />
            </div>
        </Tearsheet>
    );
};

const messages = defineMessages({
    description: {
        id: 'src.Screens.Dashboard.Team.Components.TeamCellHandler.TeamInteractions.Interactions.Details.SpecificTeamMemberView.SpecificTeamMemberView.description',
        defaultMessage:
            'Tutaj możesz zobaczyć i zarządzać danymi użytkownika.',
    },
});

export default SpecificTeamMemberViewContainer;
